import { LIVE_UPDATES_PATH } from 'constants/liveUpdates'
import { useCanOpenLiveUpdates } from 'hooks/useCanOpenLiveUpdates'
import React from 'react'
import { ESCALATIONS_PATH } from 'scenes/RecoveryAI/routes'
import { history } from 'store'
import { useStyletron } from 'styletron-react'
import FeedbackSetupSection from './FeedbackSetupSection'
import Hero from './Hero'
import type { HeroCta } from './Hero'
import HowItWorks from './HowItWorks'
import type { OverviewContent } from './resolveOverviewState'
import { resolveOverviewState } from './resolveOverviewState'
import { OVERVIEW_SECTION_STYLE } from './sectionLayout'
import { useOverviewSignals } from './useOverviewSignals'

type CtaHandler = { onClick: () => void; tabBound?: boolean }

type Props = {
  isLiveUpdatesDisabled?: boolean
}

// Switches to this page's own Feedback tab, using the same mechanism the tab
// bar itself uses (OrderFeedback/index.tsx's `onSelect` handler) — a plain
// `<a href="#feedback">` does not reliably trigger the hash-driven
// Tab.Container switch. The literal must keep matching that file's
// `Tabs.FEEDBACK`; not imported from there to avoid a circular import
// (index.tsx renders this Overview).
const goToFeedbackTab = (): void => {
  history.replace({ ...history.location, hash: 'feedback' })
}

// Switches to this page's own Settings tab, by the same mechanism and for the
// same reason as goToFeedbackTab above. The tab is rendered (and reachable by
// hash) in every state this is wired into — see index.tsx's
// `settingsTabVisible` — except while live updates are disabled, where the
// CTAs that call this are hidden.
const goToSettingsTab = (): void => {
  history.replace({ ...history.location, hash: 'settings' })
}

// Navigates to the RecoveryAI scene, routed at `/recovery_ai` in
// scenes/Routes.tsx with its own Operations nav entry.
const goToRecoveryAi = (): void => {
  history.push('/recovery_ai')
}

// Navigates straight to RecoveryAI's Escalations tab — the same deep-link
// target the Home page's own "Review escalations" CTA uses (see
// scenes/Home/NeedsAttention/useNeedsAttention.ts). AI-1394: `rai_thanx`'s
// "View escalations" used to be goToFeedbackTab, which landed on this page's
// own Feedback tab instead of RecoveryAI's actual escalation queue — the
// label promised escalations, so the destination now matches it.
const goToRecoveryAiEscalations = (): void => {
  history.push(ESCALATIONS_PATH)
}

// The Live updates page is not tab-bound, so this works while the Feedback and
// Settings tabs are greyed out.
const goToLiveUpdates = (): void => {
  history.push(LIVE_UPDATES_PATH)
}

// Per-state hero CTA destinations, looked up by the resolver's CTA key so a
// reordered or added key can never wire a label to the wrong destination.
// `tabBound` marks CTAs that switch to this page's own Feedback or Settings
// tab, which cannot open while live updates are off. Those would be dead
// clicks, so they are hidden rather than redirected: the disabled banner
// already carries the one resume link.
//
// context:
//   `new`'s "Turn on order feedback" goes to this page's own Settings tab,
//   which is where turning collection on lives, so the button lands on its
//   destination rather than doing nothing. `rai_thanx`'s "View escalations"
//   goes straight to RecoveryAI's Escalations page rather than this page's
//   own Feedback tab. `rai_all` has no such CTA: full RAI coverage hides the
//   Feedback tab a feedback-bound CTA would have pointed at.
const heroCtaHandlers = (
  kind: OverviewContent['kind']
): Record<string, CtaHandler> => {
  const key = (slot: string): string => `states.${kind}.hero.${slot}`
  switch (kind) {
    case 'new':
      return {
        [key('cta_primary')]: { onClick: goToSettingsTab, tabBound: true },
        [key('cta_secondary')]: { onClick: goToRecoveryAi },
      }
    case 'thanx':
      return {
        [key('cta_primary')]: { onClick: goToFeedbackTab, tabBound: true },
        [key('cta_secondary')]: { onClick: goToRecoveryAi },
      }
    case 'rai_thanx':
      return {
        [key('cta_primary')]: { onClick: goToRecoveryAiEscalations },
        [key('cta_secondary')]: { onClick: goToRecoveryAi },
      }
    case 'rai_all':
    case 'rai_thirdparty':
      return { [key('cta_primary')]: { onClick: goToRecoveryAi } }
    case 'thirdparty':
    case 'loading':
      return {}
  }
}

// The setup card's CTA, whose destination differs per state for the same
// reason the hero's does: `new`'s "Turn on feedback" is the same ask as its
// hero CTA and shares its destination, while `thanx` already collects and
// only offers a way into the feedback itself. The remaining states render no
// CTA on this card.
const feedbackSetupCtaHandler = (
  kind: OverviewContent['kind']
): CtaHandler | undefined => {
  switch (kind) {
    case 'new':
      return { onClick: goToSettingsTab, tabBound: true }
    case 'thanx':
      return { onClick: goToFeedbackTab, tabBound: true }
    default:
      return undefined
  }
}

const RESUME_CTA_KEY = 'turn_on_live_updates_cta'

export const withoutTabSetupCta = (
  content: OverviewContent
): OverviewContent => {
  const handlers = heroCtaHandlers(content.kind)
  return {
    ...content,
    hero: {
      ...content.hero,
      ctaKeys: content.hero.ctaKeys.filter(key => !handlers[key]?.tabBound),
    },
    feedbackSetup: feedbackSetupCtaHandler(content.kind)?.tabBound
      ? { ...content.feedbackSetup, ctaKey: undefined }
      : content.feedbackSetup,
  }
}

export const buildHeroCtas = (
  content: OverviewContent,
  canOpenLiveUpdates: boolean,
  isLiveUpdatesDisabled: boolean
): HeroCta[] => {
  const handlers = heroCtaHandlers(content.kind)
  const ctas = content.hero.ctaKeys.flatMap(key => {
    const handler = handlers[key]
    return handler ? [{ key, onClick: handler.onClick }] : []
  })
  return isLiveUpdatesDisabled && canOpenLiveUpdates && content.hero.visible
    ? [{ key: RESUME_CTA_KEY, onClick: goToLiveUpdates }, ...ctas]
    : ctas
}

// Every state's promo CTA goes to the RecoveryAI page — "Start a pilot" in
// the three states where RecoveryAI is not on yet, "Manage RecoveryAI" in
// `rai_thirdparty` where it is. Both are the same destination: the page is
// where a merchant both starts a pilot and manages an existing one, so there
// is no state left for which the button has nowhere to go.

// The Order feedback page's Overview tab. Resolves real signals via
// useOverviewSignals, feeds them through the pure resolveOverviewState, and
// renders the panes that descriptor asks for.
//
// context:
//   Every pane the descriptor can ask for is now wired. Which of them render
//   is the resolver's call, never this component's.
const Overview = ({ isLiveUpdatesDisabled = false }: Props) => {
  const [css] = useStyletron()
  const { signals, locationBadgeValues } = useOverviewSignals()
  // A viewer without MerchantSettings is sent to /unauthorized by the Live
  // updates page, so the resume CTA is not shown to them at all.
  const canOpenLiveUpdates = useCanOpenLiveUpdates()
  const resolved = resolveOverviewState(signals)
  const content = isLiveUpdatesDisabled
    ? withoutTabSetupCta(resolved)
    : resolved

  return (
    <div>
      <Hero
        content={content}
        locationBadgeValues={locationBadgeValues}
        ctas={buildHeroCtas(content, canOpenLiveUpdates, isLiveUpdatesDisabled)}
      />
      {/* One section block for the setup card AND the promo, so they share a
          single 44px/44px band and one top rule — two sibling sections would
          double both. The promo is rendered by FeedbackSetupSection rather
          than beside it, because the design insets it into the setup card in
          every state but the third-party ones; that is also why this is
          skipped on the setup descriptor alone, which the resolver never
          hides while leaving the promo visible. */}
      {content.feedbackSetup.visible && (
        <section className={css(OVERVIEW_SECTION_STYLE)}>
          <FeedbackSetupSection
            content={content}
            onCta={feedbackSetupCtaHandler(content.kind)?.onClick}
            onPromoCta={goToRecoveryAi}
          />
        </section>
      )}
      <HowItWorks
        content={content}
        isSettingsLinkEnabled={!isLiveUpdatesDisabled}
      />
    </div>
  )
}

export default Overview
